import React, { useState, useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'
import { getBloqueios, createBloqueio, deleteBloqueio, getProfissionaisAdmin, getAgendamentosNoPeriodo, getConfiguracoes } from '../../lib/supabase'
import { CORES_BLOQUEIO, MOTIVOS_BLOQUEIO, motivosBloqueioDeConfig, DURACAO_MINIMA_BLOQUEIO, duracaoMinimaBloqueioDeConfig } from '../../lib/bloqueios'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import Loading from '../../components/Loading'
import SeletorMotivoBloqueio from '../../components/SeletorMotivoBloqueio'

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

export default function Bloqueios() {
  const { tenant } = useTenant()
  const { perfil, podeFazer } = useAuth()
  const toast = useToast()
  const meuProfId = perfil?.profissional_id
  const podeGerir = podeFazer('gerir_bloqueios')        // criar / eliminar
  const podeTodos = podeFazer('bloqueios_todos')         // espaço inteiro / outros profissionais
  const [bloqueios, setBloqueios] = useState([])
  const [profissionais, setProfissionais] = useState([])
  const [motivos, setMotivos] = useState(MOTIVOS_BLOQUEIO)
  const [duracaoMinima, setDuracaoMinima] = useState(DURACAO_MINIMA_BLOQUEIO)
  const [carregando, setCarregando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [conflitos, setConflitos] = useState(null)
  const [dadosPendentes, setDadosPendentes] = useState(null)
  const [form, setForm] = useState({
    profissional_id: '',
    data_inicio: format(new Date(), 'yyyy-MM-dd'),
    hora_inicio: '09:00',
    data_fim: format(new Date(), 'yyyy-MM-dd'),
    hora_fim: '18:00',
    motivo: MOTIVOS_BLOQUEIO[0],
    cor: CORES_BLOQUEIO[0],
  })

  useEffect(() => {
    if (!tenant) return
    Promise.all([getBloqueios(tenant.id), getProfissionaisAdmin(tenant.id), getConfiguracoes(tenant.id)])
      .then(([bl, pr, cfg]) => {
        setBloqueios(bl)
        setProfissionais(pr)
        const lista = motivosBloqueioDeConfig(cfg)
        setMotivos(lista)
        setForm(p => ({ ...p, motivo: lista[0] || p.motivo }))
        setDuracaoMinima(duracaoMinimaBloqueioDeConfig(cfg))
      })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  // Sem permissão para bloquear o espaço/outros: força o profissional_id ao próprio.
  useEffect(() => {
    if (!podeTodos && meuProfId) {
      setForm(p => ({ ...p, profissional_id: meuProfId }))
    }
  }, [podeTodos, meuProfId])

  function setF(campo, valor) {
    setForm(p => ({ ...p, [campo]: valor }))
  }

  async function guardar(e) {
    e.preventDefault()
    setConflitos(null)
    setDadosPendentes(null)

    const inicio = new Date(`${form.data_inicio}T${form.hora_inicio}:00`)
    const fim = new Date(`${form.data_fim}T${form.hora_fim}:00`)
    if (fim <= inicio) {
      toast.erro('A hora de fim deve ser posterior à hora de início.')
      return
    }
    if ((fim - inicio) / 60000 < duracaoMinima) {
      toast.erro(`O bloqueio tem de ter pelo menos ${duracaoMinima} minutos.`)
      return
    }
    const dados = {
      data_hora_inicio: inicio.toISOString(),
      data_hora_fim: fim.toISOString(),
      motivo: form.motivo.trim() || 'Bloqueado',
      profissional_id: form.profissional_id || null,
      cor: form.cor || null,
    }

    setGuardando(true)
    try {
      // Verifica marcações existentes que se sobrepõem ao período do bloqueio
      const marcacoes = await getAgendamentosNoPeriodo(
        tenant.id, dados.data_hora_inicio, dados.data_hora_fim, dados.profissional_id
      )
      if (marcacoes.length > 0) {
        setConflitos(marcacoes)
        setDadosPendentes(dados)
        setGuardando(false)
        return
      }
      await criarBloqueio(dados)
    } catch (e) {
      toast.erro(e.message)
      setGuardando(false)
    }
  }

  async function criarBloqueio(dados) {
    setGuardando(true)
    try {
      const novo = await createBloqueio(tenant.id, dados)
      const profissional = profissionais.find(p => p.id === dados.profissional_id) || null
      setBloqueios(prev =>
        [...prev, { ...novo, profissionais: profissional }]
          .sort((a, b) => a.data_hora_inicio.localeCompare(b.data_hora_inicio))
      )
      setForm(p => ({ ...p, motivo: motivos[0] || 'Bloqueado', cor: CORES_BLOQUEIO[0] }))
      setConflitos(null)
      setDadosPendentes(null)
      toast.sucesso('Bloqueio criado.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  function confirmarComConflitos() {
    if (dadosPendentes) criarBloqueio(dadosPendentes)
  }

  function cancelarConflitos() {
    setConflitos(null)
    setDadosPendentes(null)
  }

  async function apagar(id) {
    if (!window.confirm('Eliminar este bloqueio?')) return
    try {
      await deleteBloqueio(tenant.id, id)
      setBloqueios(prev => prev.filter(b => b.id !== id))
    } catch (e) {
      toast.erro(e.message)
    }
  }

  // Sem permissão para ver os de todos: só os seus + globais (fecho do espaço).
  const visiveis = useMemo(
    () => !podeTodos && meuProfId
      ? bloqueios.filter(b => b.profissional_id === meuProfId || !b.profissional_id)
      : bloqueios,
    [bloqueios, podeTodos, meuProfId]
  )

  const agora = new Date()
  const futuros = visiveis.filter(b => new Date(b.data_hora_fim) > agora)
  const passados = visiveis.filter(b => new Date(b.data_hora_fim) <= agora)

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <h2 style={{ marginBottom: '6px', fontSize: '22px' }}>Bloqueios</h2>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '32px', fontSize: '14px' }}>
        Feche períodos na agenda — folgas, férias, almoços, etc.
      </p>

      {conflitos && conflitos.length > 0 && (
        <div style={{
          background: '#FFFBEB', border: '1px solid #F4C77B', borderLeft: '4px solid var(--cor-ambar)',
          borderRadius: 'var(--raio)', padding: '16px 18px', marginBottom: '20px',
        }}>
          <p style={{ fontWeight: '700', color: '#92500E', marginBottom: '4px', fontSize: '14px' }}>
            ⚠️ Existem {conflitos.length} marca{conflitos.length === 1 ? 'ção' : 'ções'} neste período
          </p>
          <p style={{ fontSize: '13px', color: '#92500E', marginBottom: '12px' }}>
            O bloqueio não cancela estas marcações. Verifica-as antes de continuar:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
            {conflitos.map(m => (
              <div key={m.id} style={{
                background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
                borderRadius: 'var(--raio)', padding: '10px 12px',
                display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
              }}>
                <span style={{ fontWeight: '700', color: 'var(--cor-principal)', fontSize: '14px', minWidth: '46px' }}>
                  {format(new Date(m.data_hora_inicio), 'HH:mm')}
                </span>
                <span style={{ flex: 1, minWidth: '140px', fontSize: '13px' }}>
                  <strong>{m.cliente_nome || 'Sem nome'}</strong>
                  {m.servicos?.nome && <span style={{ color: 'var(--cor-principal)' }}> · {m.servicos.nome}</span>}
                </span>
                {m.profissionais?.nome && (
                  <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: m.profissionais.cor_agenda || 'var(--cor-borda)', display: 'inline-block' }} />
                    {m.profissionais.nome}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={confirmarComConflitos} className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1, padding: '9px 18px', fontSize: '14px' }}>
              {guardando ? 'A guardar…' : 'Criar bloqueio à mesma'}
            </button>
            <button onClick={cancelarConflitos} className="btn-secundario" style={{ padding: '9px 18px', fontSize: '14px' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Formulário — só com permissão para gerir bloqueios */}
      {podeGerir && (
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '24px', marginBottom: '32px', boxShadow: 'var(--sombra)' }}>
        <p style={{ fontWeight: '600', color: 'var(--cor-principal)', marginBottom: '20px' }}>Novo Bloqueio</p>
        <form onSubmit={guardar}>
          <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {podeTodos && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Profissional</label>
                <select value={form.profissional_id} onChange={e => setF('profissional_id', e.target.value)} style={inputStyle}>
                  <option value="">Todas as profissionais (fecho do espaço)</option>
                  {profissionais.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Data de início</label>
              <input type="date" value={form.data_inicio} onChange={e => setF('data_inicio', e.target.value)} style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Hora de início</label>
              <input type="time" value={form.hora_inicio} onChange={e => setF('hora_inicio', e.target.value)} style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Data de fim</label>
              <input type="date" value={form.data_fim} onChange={e => setF('data_fim', e.target.value)} style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Hora de fim</label>
              <input type="time" value={form.hora_fim} onChange={e => setF('hora_fim', e.target.value)} style={inputStyle} required />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Motivo</label>
              <SeletorMotivoBloqueio motivo={form.motivo} onChange={v => setF('motivo', v)} style={inputStyle} motivos={motivos} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Cor na agenda</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {CORES_BLOQUEIO.map(c => {
                  const ativo = form.cor?.toLowerCase() === c.toLowerCase()
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setF('cor', c)}
                      title={c}
                      aria-label={`Cor ${c}`}
                      style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: c, cursor: 'pointer', padding: 0,
                        border: ativo ? '3px solid var(--cor-texto)' : '2px solid var(--cor-borda)',
                        boxShadow: ativo ? '0 0 0 2px var(--cor-branco) inset' : 'none',
                      }}
                    />
                  )
                })}
                <label title="Cor personalizada" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="color"
                    value={form.cor || '#EF4444'}
                    onChange={e => setF('cor', e.target.value)}
                    style={{ width: '32px', height: '28px', padding: 0, border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', background: 'none', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)' }}>Outra</span>
                </label>
              </div>
            </div>
          </div>
          <button type="submit" className="btn-primario" disabled={guardando} style={{ opacity: guardando ? 0.6 : 1 }}>
            {guardando ? 'A guardar…' : 'Criar Bloqueio'}
          </button>
        </form>
      </div>
      )}

      {carregando ? <Loading /> : (
        <>
          {futuros.length === 0 && passados.length === 0 && (
            <p style={{ color: 'var(--cor-texto-suave)', textAlign: 'center', padding: '32px' }}>Sem bloqueios registados.</p>
          )}

          {futuros.length > 0 && (
            <>
              <p style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>Próximos / Ativos</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                {futuros.map(b => <BloqueioCard key={b.id} b={b} onDelete={apagar} podeApagar={podeGerir && (podeTodos || b.profissional_id === meuProfId)} />)}
              </div>
            </>
          )}

          {passados.length > 0 && (
            <>
              <p style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px', color: 'var(--cor-texto-suave)' }}>Passados</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', opacity: 0.6 }}>
                {passados.slice(-10).map(b => <BloqueioCard key={b.id} b={b} onDelete={apagar} podeApagar={podeGerir && (podeTodos || b.profissional_id === meuProfId)} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

function BloqueioCard({ b, onDelete, podeApagar = true }) {
  const inicio = new Date(b.data_hora_inicio)
  const fim = new Date(b.data_hora_fim)
  const mesmoDia = format(inicio, 'yyyy-MM-dd') === format(fim, 'yyyy-MM-dd')
  const corBloqueio = b.cor || (b.profissional_id ? '#EF4444' : '#F59E0B')

  return (
    <div style={{
      background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
      borderLeft: `4px solid ${corBloqueio}`,
      borderRadius: 'var(--raio)', padding: '14px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '12px', boxShadow: 'var(--sombra)',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '600', marginBottom: '3px' }}>{b.motivo}</div>
        <div style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
          {format(inicio, "d MMM", { locale: pt })} {format(inicio, 'HH:mm')}
          {' → '}
          {mesmoDia
            ? format(fim, 'HH:mm')
            : `${format(fim, "d MMM", { locale: pt })} ${format(fim, 'HH:mm')}`
          }
        </div>
        <div style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          {b.profissionais ? (
            <>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: b.profissionais.cor_agenda, display: 'inline-block', flexShrink: 0 }} />
              {b.profissionais.nome}
            </>
          ) : (
            'Todas as profissionais'
          )}
        </div>
      </div>
      {podeApagar && (
        <button
          onClick={() => onDelete(b.id)}
          title="Eliminar"
          style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '20px', padding: '4px 8px', lineHeight: 1 }}
        >
          ×
        </button>
      )}
    </div>
  )
}
